/**
 * IBAN utilities for CZ/SK bank account conversion.
 *
 * Czech/Slovak BBAN structure: bank_code(4) + prefix(6) + account(10) = 20 chars
 * Full IBAN: country_code(2) + check_digits(2) + BBAN(20) = 24 chars
 *
 * Local format: [prefix-]account/bankCode  (e.g. "123456789/0800" or "19-2000145399/0800")
 */

/**
 * Converts a CZ or SK bank account number to IBAN.
 * Input format: [prefix-]accountNumber/bankCode
 */
export function czskAccountToIban(accountNumber: string, countryCode: "CZ" | "SK"): string {
    const slashIdx = accountNumber.lastIndexOf("/")
    if (slashIdx === -1) {
        throw new Error(`Invalid CZ/SK account format — expected [prefix-]number/bankCode`)
    }

    const bankCode = accountNumber.slice(slashIdx + 1).trim()
    const accountPart = accountNumber.slice(0, slashIdx).trim()

    let prefix = ""
    let account = accountPart

    const dashIdx = accountPart.indexOf("-")
    if (dashIdx !== -1) {
        prefix = accountPart.slice(0, dashIdx)
        account = accountPart.slice(dashIdx + 1)
    }

    const paddedBankCode = bankCode.padStart(4, "0")
    const paddedPrefix = prefix.padStart(6, "0")
    const paddedAccount = account.padStart(10, "0")

    if (!/^\d{4}$/.test(paddedBankCode)) throw new Error(`Invalid bank code: "${bankCode}"`)
    if (!/^\d{6}$/.test(paddedPrefix)) throw new Error(`Invalid prefix: "${prefix}"`)
    if (!/^\d{10}$/.test(paddedAccount)) throw new Error(`Invalid account number: "${account}"`)

    const bban = `${paddedBankCode}${paddedPrefix}${paddedAccount}` // 20 chars

    // ISO 7064 Mod 97-10 check digit calculation
    const tempIban = `${bban}${countryCode}00`
    const numeric = tempIban.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55))

    let remainder = 0
    for (const ch of numeric) {
        remainder = (remainder * 10 + parseInt(ch, 10)) % 97
    }

    const check = String(98 - remainder).padStart(2, "0")
    return `${countryCode}${check}${bban}`
}

/**
 * Converts a CZ or SK IBAN back to the native [prefix-]account/bankCode format.
 * Returns null if the IBAN is not a 24-char CZ or SK IBAN.
 */
export function ibanToCzskAccount(iban: string): string | null {
    const normalized = iban.replace(/\s/g, "").toUpperCase()
    if (
        (!normalized.startsWith("CZ") && !normalized.startsWith("SK")) ||
        normalized.length !== 24
    ) {
        return null
    }

    const bban = normalized.slice(4) // 20 chars: bankCode(4) + prefix(6) + account(10)
    const bankCode = bban.slice(0, 4)
    const prefix = bban.slice(4, 10)
    const account = bban.slice(10)

    const trimmedAccount = account.replace(/^0+/, "") || "0"
    const trimmedPrefix = prefix.replace(/^0+/, "")

    if (trimmedPrefix) {
        return `${trimmedPrefix}-${trimmedAccount}/${bankCode}`
    }
    return `${trimmedAccount}/${bankCode}`
}

/**
 * Returns true if the value is a 24-char CZ or SK IBAN.
 */
export function isCzOrSkIban(value: string): boolean {
    const normalized = value.replace(/\s/g, "").toUpperCase()
    return (normalized.startsWith("CZ") || normalized.startsWith("SK")) && normalized.length === 24
}

/**
 * Returns true if the value looks like any IBAN
 * (starts with 2 uppercase letters + 2 digits, 15-34 chars).
 */
export function looksLikeIban(value: string): boolean {
    const normalized = value.replace(/\s/g, "").toUpperCase()
    return /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(normalized)
}
